import { useCallback } from 'react';
import type { NavigateOptions as NavigateOptionsRR } from 'react-router';
import { useNavigate as useNavigateRR } from 'react-router';

import { deepClean } from '@luna/utils';

import { unsafeNavigate as pureNavigate } from './navigate';
import type { QueryParameters } from './types/NavigateOptions';
import { createQueryString } from './query/createQueryString';
import type { NavigateArgs } from './types/NavigateArgsType';
import { createUnsafeLinkPath } from './createLinkPath';
import { stripHash } from './hash/hashUtils';

export interface BaseUseNavigateOptions extends NavigateOptionsRR {
    /**
     * @deprecated - not needed anymore - latest React Router should support absolute routing out-of-the-box, starting a route with "/" is absolute as well
     */
    absolute?: boolean;
    queryParameters?: QueryParameters;
}

export type UseNavigateOptions<Path extends string> =
    NavigateArgs<Path> extends never
        ? [] | [BaseUseNavigateOptions]
        : [NavigateArgs<Path> & BaseUseNavigateOptions];

export interface NavigateFunction<Path extends string = string> {
    (to: Path, ...[options]: UseNavigateOptions<Path>): void;

    (delta: number): void;
}

/**
 * Overrides default react-router v6 useNavigate because it doesnt support absolute path routing from nested routes.
 *
 * If the absolute option is true and given url is a string, then window.location.hash will be used for changing the route.
 *
 * @deprecated resolves path params only if absolute is true
 */
export function useUnsafeNavigate<Path extends string>(): NavigateFunction<Path> {
    const navigateRR = useNavigateRR();

    return useCallback<NavigateFunction<Path>>(
        (to: Path | number, options?: BaseUseNavigateOptions) => {
            const formattedQueryParameters = createQueryString(
                deepClean((options as BaseUseNavigateOptions)?.queryParameters ?? {}),
            );
            if (
                typeof to === 'string' &&
                (options as Omit<BaseUseNavigateOptions, 'state'>)?.absolute
            ) {
                pureNavigate(to + formattedQueryParameters);
                return;
            }
            if (typeof to === 'string') {
                navigateRR(to + formattedQueryParameters, options as BaseUseNavigateOptions);
                return;
            }

            navigateRR(to as number);
        },
        [],
    );
}

/**
 * A hook that provides type-safe navigation.
 * It handles both string paths and numeric delta for history navigation.
 *
 * @returns {NavigateFunction} A function to perform navigation
 *
 * @example
 * ```tsx
 * const navigate = useNavigate();
 *
 * // Navigate to a path
 * navigate('/user/123');
 *
 * // Navigate with path parameters
 * navigate('/user/:id', { id: '123' });
 *
 * // Navigate with query parameters
 * navigate('/search', { queryParameters: { q: 'react' } });
 * ```
 */
export function useNavigate() {
    const navigateRR = useNavigateRR();

    return useCallback(
        <Path extends never>(to: Path | number, ...[options]: UseNavigateOptions<Path>) => {
            if (typeof to === 'string') {
                const formattedUrl = createUnsafeLinkPath(to, options as BaseUseNavigateOptions);
                navigateRR(stripHash(formattedUrl) || '/', options as BaseUseNavigateOptions);
                return;
            }

            navigateRR(to as number);
        },
        [],
    );
}
